import type {
  PrinterAdapter,
  PrinterDevice,
  PrinterSession,
  PrinterStatus,
  LabelTemplate,
} from '@trackeat/shared';
import { renderTemplate, type RenderedElement } from './renderer.js';

const ESC = 0x1b;
const GS = 0x1d;
const LF = 0x0a;

const ROW_TOLERANCE_MM = 2;

const groupByRow = (elements: RenderedElement[]): RenderedElement[][] => {
  const sorted = [...elements].sort((a, b) => a.y_mm - b.y_mm || a.x_mm - b.x_mm);
  const rows: RenderedElement[][] = [];
  let currentRow: RenderedElement[] = [];
  let currentY: number | null = null;
  for (const el of sorted) {
    if (currentY === null || Math.abs(el.y_mm - currentY) <= ROW_TOLERANCE_MM) {
      currentRow.push(el);
      if (currentY === null) currentY = el.y_mm;
    } else {
      rows.push(currentRow);
      currentRow = [el];
      currentY = el.y_mm;
    }
  }
  if (currentRow.length > 0) rows.push(currentRow);
  return rows.map((row) => [...row].sort((a, b) => a.x_mm - b.x_mm));
};

const pickFontSize = (pt: number | undefined): number => {
  if (!pt) return 0x00;
  if (pt >= 20) return 0x11;
  if (pt >= 14) return 0x10;
  return 0x00;
};

const encodeText = (s: string): number[] => {
  const bytes: number[] = [];
  for (const ch of s) {
    const code = ch.codePointAt(0) ?? 0x3f;
    bytes.push(code > 0x7f ? 0x3f : code);
  }
  return bytes;
};

const setBold = (on: boolean): number[] => [ESC, 0x45, on ? 0x01 : 0x00];
const setSize = (n: number): number[] => [GS, 0x21, n];
const setAlign = (n: number): number[] => [ESC, 0x61, n];
const resetLine = (): number[] => [...setBold(false), ...setSize(0x00), ...setAlign(0)];

const encodeBarcode = (data: string): number[] => {
  const bytes: number[] = [];
  bytes.push(GS, 0x68, 80);
  bytes.push(GS, 0x77, 0x02);
  bytes.push(GS, 0x48, 0x02);
  bytes.push(GS, 0x6b, 0x49, data.length);
  for (const ch of data) bytes.push(ch.charCodeAt(0) & 0xff);
  bytes.push(LF);
  return bytes;
};

const encodeQr = (data: string): number[] => {
  const bytes: number[] = [];
  const textBytes = encodeText(data);
  const storeLen = textBytes.length + 3;
  bytes.push(GS, 0x28, 0x6b, 0x04, 0x00, 0x31, 0x41, 0x32, 0x00);
  bytes.push(GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x43, 0x06);
  bytes.push(GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x45, 0x31);
  bytes.push(GS, 0x28, 0x6b, storeLen & 0xff, (storeLen >> 8) & 0xff, 0x31, 0x50, 0x30);
  for (const b of textBytes) bytes.push(b);
  bytes.push(GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x51, 0x30);
  bytes.push(LF);
  return bytes;
};

export const buildEscPos = (
  template: LabelTemplate,
  data: Record<string, unknown>,
  copies: number = 1,
): Uint8Array => {
  const rendered = renderTemplate(template, data);
  const rows = groupByRow(rendered);
  const out: number[] = [];

  for (let c = 0; c < Math.max(1, copies); c += 1) {
    out.push(ESC, 0x40);
    for (const row of rows) {
      for (const el of row) {
        out.push(...resetLine());
        if (el.font_weight === 'bold') out.push(...setBold(true));
        if (el.font_size_pt) out.push(...setSize(pickFontSize(el.font_size_pt)));
        switch (el.type) {
          case 'text':
            out.push(...encodeText(el.text ?? ''));
            out.push(0x20);
            break;
          case 'barcode':
            out.push(...encodeBarcode(el.barcode_data ?? ''));
            break;
          case 'qr':
            out.push(...encodeQr(el.qr_data ?? ''));
            break;
          default:
            break;
        }
      }
      out.push(LF);
    }
    out.push(LF, LF, LF);
    out.push(GS, 0x56, 0x00);
  }

  return Uint8Array.from(out);
};

export class EscPosAdapter implements PrinterAdapter {
  readonly driver = 'escpos' as const;

  async discover(): Promise<PrinterDevice[]> {
    // TODO: BLE scan for SPP profile (common ESC/POS thermal printers).
    return [];
  }

  async connect(device: PrinterDevice): Promise<PrinterSession> {
    // TODO: open BLE Classic SPP or TCP 9100 socket.
    return { deviceId: device.id, driver: this.driver };
  }

  async disconnect(_session: PrinterSession): Promise<void> {
    // TODO: close socket.
  }

  async printLabel(
    _session: PrinterSession,
    template: LabelTemplate,
    data: Record<string, unknown>,
    copies: number = 1,
  ): Promise<void> {
    const bytes = buildEscPos(template, data, copies);
    // TODO: write `bytes` over the session transport.
    void bytes;
  }

  async getStatus(_session: PrinterSession): Promise<PrinterStatus> {
    // TODO: DLE EOT n query (ESC/POS real-time status).
    return {
      online: true,
      paper_ok: true,
      cover_closed: true,
      battery_pct: null,
      error_code: null,
    };
  }
}
